import { type MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useHistory } from "react-router-dom";
import { type CloudAnalyticsModelWidgetModel, type Key, WidgetState } from "@doitintl/cmp-models";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { WithModel } from "@doitintl/models-any";

import { useApiContext } from "../../../api/context";
import { reportsHelpURLs } from "../../../assets/urls";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useDashboardProcessingContext } from "../../../Context/DashboardProcessingContext";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { refreshWidget } from "../../../Pages/CloudAnalytics/handlers/updateWidgets";
import { getReportPath } from "../../../Pages/CloudAnalytics/report/utils";
import { WidgetsContext } from "../../../Pages/Customer/Dashboards/WidgetsGrid/WidgetsGrid";
import { consoleErrorWithSentry } from "../../../utils";
import { getDateTimeFromFirestoreTimestamp, jsUcfirst } from "../../../utils/common";
import mixpanel from "../../../utils/mixpanel";
import { parseWidgetDocId } from "../../../utils/widgets";
import useUnmountEffect from "../../hooks/useUnmountEffect";
import InfoMessageWidgetCard from "../InfoMessageWidgetCard";
import { SkeletonCard } from "../SkeletonCard";
import { type WidgetItemWithWidgetIdProps } from "../types";
import { WidgetMessageCardContent } from "../WidgetCards/Common/WidgetMessageCardContent";
import { CardReportHeader } from "./CardReportHeader";
import { CloudReportCardContent } from "./CloudReportCardContent";
import { CloudReportCardMenu } from "./CloudReportCardMenu";
import { CloudReportHeaderAction } from "./CloudReportHeaderAction";
import { useEnhancedWidgetData } from "./hooks";
import { ReportDeletedErrorCard } from "./ReportDeletedErrorCard";
import { cleanDashboardLensWidgetName } from "./utils";

type Props = Omit<WidgetItemWithWidgetIdProps, "fallbackComponent"> & {
  cloudAnalyticsKey?: Key;
  reportWithData?: WithModel<CloudAnalyticsModelWidgetModel>;
};

const bottomTimeStampPadding = 26;

export default function CloudReportCard({ widgetId, cloudAnalyticsKey, reportWithData, widgetHeight = 500 }: Props) {
  const api = useApiContext();
  const history = useHistory();
  const [widgets, , disableAutoRefresh] = useContext(WidgetsContext);
  const widget = useMemo(() => widgets.find((w) => w.name.endsWith(widgetId)), [widgetId, widgets]);

  const { currentUser, isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const [selectedFilter, setSelectedFilter] = useState<string>("All");
  const timeoutId = useRef<any>(-1);

  const { customer } = useCustomerContext();
  const presentationMode = isCustomerInPresentationMode(customer);

  const { data, loading: widgetDocLoading } = useEnhancedWidgetData(widgetId, reportWithData);
  const showHeader = !reportWithData;
  const rawReport = data?.rawReport ?? undefined;
  const reportHasData = Boolean(rawReport?.rawRows?.length);
  const [handledAutoRefresh, setHandledAutoRefresh] = useState(disableAutoRefresh);
  const finishedLoadingData = (data && !widgetDocLoading) || (handledAutoRefresh && !widgetDocLoading);

  const [largeReportError, setLargeReportError] = useState(false);

  const { collaborators, description, name, isPublic, reportId, timeRefreshed } = useMemo(
    () => ({
      collaborators: rawReport?.collaborators ?? [],
      description: rawReport?.description ?? "",
      name: rawReport?.name ?? "",
      isPublic: rawReport?.isPublic ?? false,
      reportId: rawReport?.reportId ?? "",
      timeRefreshed: rawReport?.timeRefreshed,
    }),
    [rawReport]
  );
  const [refreshing, setRefreshing] = useState(widget?.state === WidgetState.Processing);
  const [lastRefreshTime, setLastRefreshTime] = useState(-1);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { refreshTime, setDashboardWidgetStates } = useDashboardProcessingContext();

  const isRecentlyRefreshed = useMemo(() => {
    if (!timeRefreshed) {
      return false;
    }
    return Date.now() - timeRefreshed.toMillis() < 10 * 60 * 1000;
  }, [timeRefreshed]);

  useUnmountEffect(() => {
    clearTimeout(timeoutId.current);
  });

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const hasAccessToReport = useMemo(
    () => isDoitEmployee || isPublic || collaborators?.some((collaborator) => collaborator.email === currentUser.email),
    [isDoitEmployee, isPublic, collaborators, currentUser.email]
  );

  const trackFullReport = useCallback(() => {
    if (name === "[GKE Lens] Unallocated Resources by Cluster") {
      mixpanel.track("dashboards.gke-lens.cost-allocation-clusters");
    }
    handleCloseMenu();
    mixpanel.track("dashboard.widget.launch", { name });
  }, [name]);

  const callToRefresh = useCallback(async () => {
    if (refreshing || Date.now() - lastRefreshTime < 2000) {
      return;
    }

    setRefreshing(true);

    const { customerId, reportId } = parseWidgetDocId(widgetId);

    try {
      const response = await refreshWidget(api, customerId, reportId);
      setLastRefreshTime(Date.now());
      if (response.status === 200) {
        return true;
      } else {
        if (response.status === 507) {
          setLargeReportError(true);
        }
        consoleErrorWithSentry(response);
        return false;
      }
    } finally {
      setRefreshing(false);
    }
  }, [api, lastRefreshTime, refreshing, widgetId]);

  useEffect(() => {
    if (refreshTime && Date.now() - refreshTime < 1000) {
      callToRefresh();
    }
  }, [callToRefresh, refreshTime]);

  useEffect(() => {
    if (!data && !widgetDocLoading) {
      if (handledAutoRefresh) {
        setDashboardWidgetStates(widgetId, false);
      } else {
        callToRefresh().then((success) => {
          if (success) {
            timeoutId.current = setTimeout(() => {
              setHandledAutoRefresh(true);
              setDashboardWidgetStates(widgetId, false);
            }, 90 * 1000);
          } else if (success === false) {
            setHandledAutoRefresh(true);
          }
        });
      }
    }
  }, [
    callToRefresh,
    widgetDocLoading,
    handledAutoRefresh,
    data,
    setDashboardWidgetStates,
    widgetId,
    disableAutoRefresh,
  ]);

  useEffect(() => {
    if (data) {
      setHandledAutoRefresh(true);
      setDashboardWidgetStates(widgetId, true);
      clearTimeout(timeoutId.current);
    }
  }, [data, setDashboardWidgetStates, widgetId]);

  const reportPath = getReportPath(customer.id, reportId);
  const helpUrl = reportsHelpURLs[reportId];

  const handleOpenReport = useCallback(() => {
    history.push(reportPath, { prevPage: history.location.pathname });
  }, [history, reportPath]);

  const menu = useMemo(
    () =>
      showHeader ? (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
          }}
        >
          <CloudReportCardMenu
            anchorEl={anchorEl}
            hasAccessToReport={hasAccessToReport}
            helpUrl={helpUrl}
            open={open}
            reportPath={reportPath}
            thisReportHasData={reportHasData}
            trackFullReport={trackFullReport}
            handleCloseMenu={handleCloseMenu}
            handleOpenMenu={handleOpenMenu}
          />
        </Stack>
      ) : undefined,
    [showHeader, anchorEl, hasAccessToReport, helpUrl, open, reportPath, reportHasData, trackFullReport]
  );

  const onChangeFilter = useCallback(
    (e) => {
      e.stopPropagation();
      setSelectedFilter(e.target.value);
    },
    [setSelectedFilter]
  );

  if (!finishedLoadingData) {
    return <SkeletonCard widgetHeight={widgetHeight} />;
  }

  if (finishedLoadingData && !data) {
    if (largeReportError) {
      return (
        <InfoMessageWidgetCard
          name="cloudReports"
          widgetHeight={widgetHeight}
          title={name}
          subheader={jsUcfirst(description)}
          message="Report cannot be displayed due to number of data points"
        />
      );
    }
    return <ReportDeletedErrorCard title={name} subHeader={jsUcfirst(description)} widgetHeight={widgetHeight} />;
  }

  const lastUpdatedText = timeRefreshed
    ? `Last updated: ${
        Date.now() - timeRefreshed.toMillis() < 60 * 1000
          ? "less than a minute ago"
          : getDateTimeFromFirestoreTimestamp(timeRefreshed).toRelative()
      }`
    : "Not updated yet";

  return (
    <Card>
      {showHeader && (
        <CardReportHeader
          action={
            <CloudReportHeaderAction
              onChangeFilter={onChangeFilter}
              selectedFilter={selectedFilter}
              rawReport={rawReport}
              cloudAnalyticsKey={cloudAnalyticsKey}
              menu={menu}
            />
          }
          name={cleanDashboardLensWidgetName(name)}
          reportUrl={reportPath}
          hasAccessToReport={hasAccessToReport}
          description={description}
          hasFilter={Boolean(cloudAnalyticsKey)}
        />
      )}

      <Box
        sx={{
          py: 0.5,
          height: widgetHeight,
          position: "relative",
        }}
      >
        {reportHasData ? (
          <>
            <Box sx={{ height: widgetHeight - bottomTimeStampPadding }}>
              <CloudReportCardContent
                rawReport={rawReport}
                selectedFilter={selectedFilter}
                cloudAnalyticsKey={cloudAnalyticsKey}
              />
            </Box>
            <Box
              sx={{
                height: bottomTimeStampPadding,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                pr: 1.25,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {lastUpdatedText}
                </Typography>
                {refreshing ? (
                  <Box sx={{ pl: 1 }}>
                    <CircularProgress disableShrink color="primary" size={14} thickness={4.8} />
                  </Box>
                ) : (
                  <Tooltip
                    title={isRecentlyRefreshed ? "Widget was recently updated, try again later" : "Update widget data"}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={presentationMode || isRecentlyRefreshed}
                        onClick={() => {
                          callToRefresh();
                        }}
                        data-cy="report-refresh-button"
                      >
                        <RefreshIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </>
        ) : (
          <WidgetMessageCardContent
            widgetHeight={widgetHeight}
            message="This report's configuration returned no results."
            extra={
              <Stack direction="column" spacing={1} sx={{ mt: 2 }} alignItems="center">
                <Button variant="outlined" onClick={handleOpenReport}>
                  Open in Reports
                </Button>
                <Button
                  variant="text"
                  onClick={callToRefresh}
                  disabled={refreshing || presentationMode || isRecentlyRefreshed}
                  startIcon={refreshing ? <CircularProgress size={16} /> : null}
                  data-cy="report-refresh-button"
                >
                  Refresh data
                </Button>
              </Stack>
            }
          />
        )}
      </Box>
    </Card>
  );
}
